// How a tutorial run ends (#1854): what it records about the core, and how the
// tutorial project is thrown away. The network sits behind deps, as in
// `tutorialProject.ts`.

import { TUTORIAL_CHAPTER_IDS } from '@/data/userTutorial';
import type { RunOutcome, TutorialChapterId, TutorialCoreState, UserTutorial } from '@/types';

/** The core's state after a run. A finished core stays finished: replaying it
 *  from the hub and leaving half-way changes nothing. */
export function coreAfterRun(current: TutorialCoreState, outcome: RunOutcome): TutorialCoreState {
    return current === 'done' ? 'done' : outcome;
}

/** The finished chapters after a chapter run. Only a finish records one, and
 *  a replay of a finished chapter returns the same list: nothing to write. */
export function chaptersAfterRun(
    current: readonly TutorialChapterId[],
    chapter: TutorialChapterId,
    outcome: RunOutcome,
): readonly TutorialChapterId[] {
    if (outcome !== 'done' || current.includes(chapter)) return current;
    return TUTORIAL_CHAPTER_IDS.filter(id => id === chapter || current.includes(id));
}

export interface DeleteTutorialProjectDeps {
    /** The soft delete: to the trash, which the tutorial never restores from. */
    trashProject(projectId: number): Promise<void>;
    readTutorial(): Promise<UserTutorial>;
    /** Writes `project_id: null` and nothing else: `core` and `chapters` keep their values. */
    clearProjectId(): Promise<void>;
}

/**
 * «Удалить учебный проект». The trash comes first: a project that is still
 * there stays the tutorial's. A stored id the trash already took would only
 * read as `missing` and seed fresh anyway, so a failed clear loses nothing.
 */
export async function deleteTutorialProject(projectId: number, deps: DeleteTutorialProjectDeps): Promise<void> {
    await deps.trashProject(projectId);
    const { project_id: storedId } = await deps.readTutorial();
    if (storedId === projectId) await deps.clearProjectId();
}
